import React, {useEffect, useMemo, useState} from 'react'
import {observer} from 'mobx-react-lite'
import {useParams, useLocation} from 'react-router-dom'
import {Box, Container, Stack} from '../../vanilla'
import {Layout} from '../../components/layout'
import {Seo} from '../../components/seo'
import {Breadcrumbs, Crumb} from '../../components/breadcrumbs'
import {ProductDetail} from '../../components/product-detail'
import {useBasketStore, useCategoryStore, useContentStore, useGlobalStore, useProductStore, useCustomerStore} from '../../store/hooks/useStore'
import {TObservablePage} from '../../types/commerce'
import {flowResult} from 'mobx'
import {useCategory} from '../../store/hooks/useCategory'
import {Recommender} from '../../components/recommendations'
import {isServer} from '../../utils/utils'
import {PDPAwardSlot, ProductInformation} from '../../types/cms'
import {buildProductModelStructured} from '../../utils/structured-data'
import LazyLoad from 'react-lazyload'
import {usePageDataTracking} from '../../analytics/page-tracking'
import {useItemDataTracking} from '../../analytics/view-item-tracking'
import {getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import {formatProductName, isMobile} from '../../utils/utils'
import PageNotFound from '../../pages/page-not-found'
import {useItemPrice} from '../../components/algolia/util/useItemPrice'
import {transformHit} from '../../components/algolia/util/transformHit'
import {Hit} from '../../store/ProductStore'

interface PDPBreadcrumbsProps {
  productName?: string
  productId?: string
  categoryId?: string
  className: string
}

const PDPBreadcrumbs = (props: PDPBreadcrumbsProps) => {
  const { productName, categoryId, productId, className } = props
  const category = useCategory(categoryId)

  const crumbs: Crumb[] = (category?.parentCategoryTree as Crumb[]) || []
  crumbs.forEach((crumb) => {
    crumb.className = 'pdp-breadcrumb'
  })

  // If on a mobile, we don't want to show the product name in the breadcrumbs
  const isMobileFlag = isMobile()

  return (
    <Breadcrumbs breadcrumbs={isMobileFlag ? [...crumbs] : [...crumbs, { id: productId, name: productName, url: undefined, className: className } as Crumb]} />
  )
}

const ProductPage: TObservablePage = observer(() => {
  const { productId }: { productId: string } = useParams()
  const { productsById, getPdpAwardSlot } = useProductStore()
  const cms = useContentStore()
  const productInformationContent = cms.contentById['product-information'] as ProductInformation
  const [pdpAwardsSlot, setPdpAwardsSlot] = useState<PDPAwardSlot | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const categoryStore = useCategoryStore()
  const product = productId ? productsById[productId] : undefined
  const isBundle = product?.bundle
  const excludeFromSearchEngineIndexing = product?.excludeFromSearchEngineIndexing
  const basketStore = useBasketStore()
  const sendPageData = usePageDataTracking()
  const sendItemData = useItemDataTracking()
  const {selectedStoreId, selectedPriceDayOffset, selectedRegion} = useGlobalStore()
  const [showPageNotFound, setShowPageNotFound] = useState(false)
  const getCategories = async () => {
    await categoryStore.fetchAllCategories()
    setIsLoading(false)
  }
  const location = useLocation()

  useEffect(() => {
    if (categoryStore.status === 'idle') {
      getCategories()
    }
  }, [])

  let pageType = ''
  let pageCategory = ''
  let pageSubCategory = ''

  useEffect(() => {
    if (categoryStore.status !== 'done') {
      return
    }
    const productCategory =
      product?.primaryCategoryId && product?.primaryCategoryId in categoryStore.categories ? categoryStore.categories[product?.primaryCategoryId] : undefined

    if (productCategory) {
      pageType = 'product'

      const { isTopLevelCategory, parentCategoryTree, name } = productCategory
      if (!isTopLevelCategory) {
        pageCategory = parentCategoryTree?.[0].name || ''
        pageSubCategory = name || ''
      } else {
        pageCategory = name || ''
      }
    }
    sendPageData(pageType, pageCategory, pageSubCategory)
  }, [isLoading])

  useEffect(() => {
    if (!cms.contentById['product-information']) {
      cms.fetchItemByKey('product-information')
    }
  }, [product])

  useEffect(() => {
    if (!product || !selectedStoreId || !selectedRegion) return
    // Send product data to GTM when product and basket has loaded 
    (async function getBadgeData() {
      if (product) {
        const badges = await flowResult(cms.fetchBadgesForHits([product]))
        sendItemData(transformHit(product as unknown as Hit, selectedPriceDayOffset, selectedStoreId, selectedRegion), badges)
      }
    })()
  }, [product, selectedStoreId, selectedRegion])

  useEffect(() => {
    (async function () {
      if (product) {
        const pdpAwardSlot = await flowResult(
          getPdpAwardSlot(product.id, product.categoryPageId ?? []),
        )
        setPdpAwardsSlot(pdpAwardSlot ?? null)
      }
    })()
  }, [product])

  const {lowestPrice, lowestWasPrice} = useItemPrice(product ?? null)
  const {currentCustomerGroups} = useCustomerStore()

  const structuredData = useMemo(() => {
    if (!location.pathname || !product || !currentCustomerGroups) {
      return
    }

    return product ? [buildProductModelStructured(product, lowestPrice, lowestWasPrice)] : undefined
  }, [location.pathname, product, lowestPrice, lowestWasPrice, currentCustomerGroups])

  useEffect(() => {
    if (product && typeof window !== 'undefined' && window.location.pathname === `/p/${product.id}`) {
      window.location.href = `${getAppOrigin()}${`/p/${formatProductName(product.name)}/${product.id}.html`}`
    }
  }, [productId])

  let productPageBaseUrl = ''
  if (product && product.name && product.id) {
    productPageBaseUrl = `/p/${formatProductName(product.name)}/${product.id}.html`
  } else if (typeof window !== 'undefined') {
    productPageBaseUrl = window.location.pathname.split('?')[0]
  }

  useEffect(() => {
    if (!isLoading && !product) {
      setShowPageNotFound(true)
    }
  }, [isLoading, product])

  if (showPageNotFound) {
    return (
      <Layout>
        <Seo title="404" />
        <PageNotFound />
      </Layout>
    )
  }

  return (
    <Layout key={productId}>
      <Seo
        title={`${product?.name} | Iceland Foods`}
        description={`Buy ${product?.name} online at Iceland. Free next day delivery (minimum spend applies).`}
        structuredData={structuredData}
        canonical={productPageBaseUrl}
        noIndex={excludeFromSearchEngineIndexing}
      />
      <Box paddingY={['12px', '12px', '24px']}>
        <Stack spacing="0px">
          <Container paddingX={['14px', '8px', '16px']} paddingBottom={['12px', '16px']}>
            <PDPBreadcrumbs
              categoryId={product?.primaryCategoryId}
              productName={product?.name}
              productId={product?.id}
              className="pdp-breadcrumbs"
            />
          </Container>
          <Stack spacing="64px">
            <ProductDetail
              productId={productId || ''}
              productInformation={productInformationContent}
              pdpAwardSlot={pdpAwardsSlot}
              isBundle={isBundle}
            />
            <LazyLoad>
              <Recommender
                productId={productId}
                recommender="product-to-product"
                name="Customers also viewed..."
                key={productId}
              />
            </LazyLoad>
          </Stack>
        </Stack>
      </Box>
    </Layout>
  )
})

ProductPage.getTemplateName = () => 'product-page'

ProductPage.shouldGetProps = ({ previousLocation, location }) => {
  return (
    !previousLocation ||
    previousLocation.pathname !== location.pathname ||
    previousLocation.search !== location.search
  )
}

ProductPage.getProps = async ({ params, store, res }) => {
  const { productId } = params
  if (isServer) {
    try {
      const fetchedIds = await flowResult(store.productStore.fetchProducts([productId], true))
      if (!fetchedIds.includes(productId)) {
        res.status(404)
        return
      }

      await flowResult(store.contentStore.fetchItemByKey('product-information'))
    } catch (error) {
      console.error(error)
    }
  }
}

export default ProductPage